import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { InstitutionMaterial, InstitutionMaterialForm, InstitutionMaterialSearchForm } from 'entities/institution_material'
import { Institution } from 'entities/institution'
import { Material } from 'entities/material'

type InstitutionMaterialsResponse = PagingResponse & {
    results: InstitutionMaterial[]
}

export function useFetchInstitutionMaterialsApi(
    searchForm?: Form<InstitutionMaterialSearchForm>
): IndexApiSet<InstitutionMaterialsResponse> & { execute: () => void } {
    const apiPath = 'institution_materials/'
    const api = useIndexApi<InstitutionMaterialsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function usePostInstitutionMaterialApi(): ApiSet<BaseResponse> & { execute: (form: Form<InstitutionMaterialForm>) => void } {
    const apiSet = usePostApi<BaseResponse, InstitutionMaterialForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<InstitutionMaterialForm>) => {
        const apiPath = `institution_materials/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutInstitutionMaterialApi(): ApiSet<BaseResponse> & { execute: (form: Form<InstitutionMaterialForm>) => void } {
    const apiSet = usePutApi<BaseResponse, InstitutionMaterialForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<InstitutionMaterialForm>) => {
        const apiPath = `institution_materials/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutInstitutionMaterialsApi(): ApiSet<BaseResponse> & { execute: (form: Form<InstitutionMaterialForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, InstitutionMaterialForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<InstitutionMaterialForm[]>) => {
        const apiPath = `institution_materials/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutToNotApplicableInstitutionMaterialsApi(): ApiSet<BaseResponse> & { execute: (form: Form<InstitutionMaterialForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, InstitutionMaterialForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<InstitutionMaterialForm[]>) => {
        const apiPath = `institution_materials/bulk_update_to_not_applicable/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteInstitutionMaterialApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `institution_materials/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export type CreateTaskFromMaterialForm = {
    userGroupId?: number
    institutions?: Institution[]
    materialInfos?: { material?: Material; number?: number }[]
}

export function useCreateTasksFromMaterialApi(): ApiSet<BaseResponse> & { execute: (form: Form<CreateTaskFromMaterialForm>) => void } {
    const apiSet = usePostApi<BaseResponse, CreateTaskFromMaterialForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<CreateTaskFromMaterialForm>) => {
        const apiPath = `institution_materials/create_tasks/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}
